import {useEffect, useRef, useState} from 'react'
import {Modal, Button, Form, Row, Col, Nav} from 'react-bootstrap'
import Select from 'react-select'
import {GENDER_OPTIONS, INITIAL_CONTACT_FILTERS} from '../../../helpers/consts'
import {ContactFilters, ContactSavedFilter} from '../contactsTypes'
import {parseId} from '../../../helpers'

interface ModalFiltersProps {
  isOpen: boolean
  onClose: () => void
  onApply: (filters: any) => void
  filters: ContactFilters
  setFilters: React.Dispatch<React.SetStateAction<ContactFilters>>
  savedFilters?: ContactSavedFilter[]
  onSaveFilter?: (name: string, filters: ContactFilters) => Promise<ContactSavedFilter | null>
  onUpdateFilter?: (id: string, name: string, filters: ContactFilters) => Promise<ContactSavedFilter | null>
  onDeleteFilter?: (id: string) => Promise<boolean>
  users: any[]
  states: any[]
  immigrationStatuses: { label: string; value: string }[]
  signatureForms: any[]
  carriers: any[]
  policyStatuses: { id: string; status: string }[]
  allowedImmigrationStatuses?: string[]
}

export const ModalFilters: React.FC<ModalFiltersProps> = ({
  isOpen,
  onClose,
  onApply,
  filters,
  setFilters,
  savedFilters = [],
  onSaveFilter,
  onUpdateFilter,
  onDeleteFilter,
  users,
  states,
  immigrationStatuses,
  signatureForms,
  carriers,
  policyStatuses,
  allowedImmigrationStatuses,
}) => {
  const [activeTab, setActiveTab] = useState<'filters' | 'saved'>('filters')
  const [savedTabMode, setSavedTabMode] = useState<'save' | 'manage'>('manage')

  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState<string | null>(null)
  const [savedFilterName, setSavedFilterName] = useState('')
  const [shouldFocusSavedName, setShouldFocusSavedName] = useState(false)
  const savedNameInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!isOpen) return
    if (!selectedSavedFilterId) {
      setSavedFilterName('')
    }
  }, [isOpen, selectedSavedFilterId])

  useEffect(() => {
    if (activeTab !== 'saved' || !shouldFocusSavedName) return
    if (savedNameInputRef.current) {
      savedNameInputRef.current.focus()
      savedNameInputRef.current.select()
      setShouldFocusSavedName(false)
    }
  }, [activeTab, shouldFocusSavedName])

  const goToSavedTab = (mode: 'save' | 'manage') => {
    setActiveTab('saved')
    setSavedTabMode(mode)
    setShouldFocusSavedName(mode === 'save')
  }

  const handleSelectChange = (field: string, selected: any) => {
    let value = selected?.value
    if (value === 'true') value = true
    else if (value === 'false') value = false
    else if (value === '') value = undefined

    setFilters((prev: any) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleInputChange = (field: string, value: string) => {
    setFilters((prev: any) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleImmigrationStatusChange = (selected: any) => {
    const values = Array.isArray(selected)
      ? selected.map((opt: any) => opt?.value).filter(Boolean)
      : selected?.value
        ? [selected.value]
        : []

    setFilters((prev: any) => ({
      ...prev,
      immigrationStatus: values,
    }))
  }

  const handleSubmit = () => {
    const parsedFilters = {
      ...filters,
      ...(filters.carrier && { carrier: parseId(filters.carrier) }),
      ...(filters.signatureForm && { signatureForm: parseId(filters.signatureForm) }),
      ...(filters.policyType && { policyType: parseId(filters.policyType) }),
    }

    console.log('🚀 Filtros aplicados (parseados) desde el modal:', parsedFilters)
    onApply(parsedFilters)
    onClose()
  }

  const usersOptions = users.map((u) => ({value: u.id, label: `${u.fullName} - ${u.email}`}))
  const selectedUser = usersOptions.find((opt) => String(opt.value) === String(filters.user)) || null

  const languageOptions = [
    {value: 'Spanish', label: 'Spanish'},
    {value: 'English', label: 'English'},
  ]
  const selectedLanguage =
    languageOptions.find((opt) => opt.value === filters.preferredLanguage) || null

  const genderOptions = GENDER_OPTIONS.map((s) => ({value: s.value, label: s.label}))
  const selectedGender = genderOptions.find((opt) => opt.value === filters.gender) || null

  const stateOptions = states.map((s) => ({value: s.code, label: s.name}))
  const selectedState = stateOptions.find((opt) => opt.value === filters.state) || null

  const immigrationOptions = allowedImmigrationStatuses?.length
    ? immigrationStatuses.filter((opt) => allowedImmigrationStatuses.includes(opt.value))
    : immigrationStatuses
  const selectedImmigrationValues = Array.isArray(filters.immigrationStatus)
    ? filters.immigrationStatus
    : filters.immigrationStatus
      ? [filters.immigrationStatus]
      : []
  const selectedImmigration = immigrationOptions.filter((opt) =>
    selectedImmigrationValues.includes(opt.value)
  )

  const signatureStatusOptions = [
    {value: 'SIGNED', label: 'Signed'},
    {value: 'NOT_SIGNED', label: 'Did not sign'},
  ]
  const selectedSignatureStatus =
    signatureStatusOptions.find((opt) => opt.value === filters.signatureStatus) || null

  const signatureFormOptions = signatureForms.map((s) => ({value: s.id, label: s.name}))
  const selectedSignatureForm =
    signatureFormOptions.find((opt) => String(opt.value) === String(filters.signatureForm)) || null

  const carrierOptions = carriers.map((c) => ({value: c.id, label: c.name}))
  const selectedCarrier =
    carrierOptions.find((opt) => String(opt.value) === String(filters.carrier)) || null

  const policyStatusOptions = policyStatuses.map((s) => ({value: s.status, label: s.status}))
  const selectedPolicyStatus =
    policyStatusOptions.find((opt) => opt.value === filters.policyStatus) || null

  const savedFilterOptions = savedFilters.map((filter) => ({
    value: filter.id,
    label: filter.name,
  }))

  const selectedSavedFilterOption =
    savedFilterOptions.find((opt) => opt.value === selectedSavedFilterId) || null

  const handleSavedFilterSelect = (selected: any) => {
    if (!selected) {
      setSelectedSavedFilterId(null)
      setSavedFilterName('')
      return
    }

    const match = savedFilters.find((filter) => filter.id === selected.value)
    if (!match) return

    setSelectedSavedFilterId(match.id)
    setSavedFilterName(match.name)
    setFilters(() => ({...match.filters}))
  }

  const handleSaveFilter = async () => {
    if (!onSaveFilter) return
    const saved = await onSaveFilter(savedFilterName, filters)
    if (!saved) return
    setSelectedSavedFilterId(saved.id)
    setSavedFilterName(saved.name)
  }

  const handleSaveAndApply = async () => {
    if (onSaveFilter) {
      const saved = await onSaveFilter(savedFilterName, filters)
      if (!saved) return
      setSelectedSavedFilterId(saved.id)
      setSavedFilterName(saved.name)
    }
    handleSubmit()
  }

  const handleUpdateFilter = async () => {
    if (!onUpdateFilter || !selectedSavedFilterId) return
    const saved = await onUpdateFilter(selectedSavedFilterId, savedFilterName, filters)
    if (!saved) return
    setSavedFilterName(saved.name)
  }

  const handleDeleteFilter = async () => {
    if (!onDeleteFilter || !selectedSavedFilterId) return
    const confirmed = window.confirm('Delete this saved filter?')
    if (!confirmed) return
    const deleted = await onDeleteFilter(selectedSavedFilterId)
    if (!deleted) return
    setSelectedSavedFilterId(null)
    setSavedFilterName('')
  }

  const handleClearFilters = () => {
    setSelectedSavedFilterId(null)
    setSavedFilterName('')
    setFilters(() => ({...INITIAL_CONTACT_FILTERS}))
  }

  const handleApplySavedFilter = () => {
    if (!selectedSavedFilterId) return
    onApply(filters)
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={onClose} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Advanced Filters</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='d-flex align-items-center justify-content-between mb-3 border-bottom pb-2'>
          <Nav
            variant='tabs'
            className='border-bottom-0'
            activeKey={activeTab}
            onSelect={(key) => {
              const nextTab = (key as 'filters' | 'saved') || 'filters'
              setActiveTab(nextTab)
              if (nextTab === 'saved') {
                setSavedTabMode('manage')
              }
            }}
          >
            <Nav.Item>
              <Nav.Link eventKey='filters'>Filters</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='saved'>Saved filters</Nav.Link>
            </Nav.Item>
          </Nav>
          {activeTab === 'filters' ? (
            <div className='d-flex gap-2'>
              <Button variant='light' className='px-3' onClick={handleClearFilters}>
                Clear
              </Button>
              <Button variant='dark' className='px-3' onClick={() => goToSavedTab('save')}>
                <i className='bi bi-save me-1' /> Save Filter
              </Button>
              <Button variant='primary' className='px-3' onClick={handleSubmit}>
                <i className='bi bi-search me-1' /> Apply Filters
              </Button>
            </div>
          ) : null}
        </div>
        <Form>
          {activeTab === 'saved' ? (
            <>
              <div className='mb-2'>
                <h5 className='mb-1'>Manage saved filters</h5>
                {savedTabMode === 'manage' ? (
                  <div className='text-muted'>Select a saved filter for quick access to data</div>
                ) : null}
              </div>
              {savedTabMode === 'save' ? (
                <Row className='mb-3 align-items-end'>
                  <Col md={8}>
                    <Form.Label>Filter name</Form.Label>
                    <Form.Control
                      ref={savedNameInputRef}
                      value={savedFilterName}
                      placeholder='Enter filter name'
                      onChange={(e) => setSavedFilterName(e.target.value)}
                    />
                    <div className='text-muted mt-2'>
                      Save the current filters so you can reuse them later.
                    </div>
                  </Col>
                  <Col md={4}>
                    <Form.Label>&nbsp;</Form.Label>
                    <div className='d-flex justify-content-end'>
                      <Button
                        variant='dark'
                        className='px-3'
                        onClick={handleSaveAndApply}
                        disabled={!savedFilterName.trim()}
                      >
                        Save and apply
                      </Button>
                    </div>
                  </Col>
                </Row>
              ) : (
                <Row className='mb-2 align-items-center'>
                  <Col md={8}>
                    <Form.Label>Saved Filters</Form.Label>
                    <Select
                      value={selectedSavedFilterOption}
                      options={savedFilterOptions}
                      onChange={handleSavedFilterSelect}
                      isClearable
                      placeholder='Select saved filter'
                    />
                  </Col>
                  <Col md={4}>
                    <div className='d-flex flex-wrap justify-content-end gap-2'>
                      <Button variant='light' className='px-3' onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        variant='outline-danger'
                        className='px-3'
                        onClick={handleDeleteFilter}
                        disabled={!selectedSavedFilterId}
                      >
                        Delete
                      </Button>
                      <Button
                        variant='dark'
                        className='px-3'
                        onClick={handleApplySavedFilter}
                        disabled={!selectedSavedFilterId}
                      >
                        Apply saved filter
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}
              <hr />
            </>
          ) : null}
          {activeTab === 'filters' ? (
            <>
              <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>User</Form.Label>
              <Select
                value={selectedUser}
                options={usersOptions}
                onChange={(selected) => handleSelectChange('user', selected)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Preferred Language</Form.Label>
              <Select
                value={selectedLanguage}
                options={languageOptions}
                onChange={(selected) => handleSelectChange('preferredLanguage', selected)}
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>Date of Birth Month</Form.Label>
              <Form.Select
                value={filters.birthMonth}
                onChange={(e) => handleInputChange('birthMonth', e.target.value)}
              >
                <option value=''>Select one</option>
                {[
                  'January','February','March','April','May','June',
                  'July','August','September','October','November','December',
                ].map((month, index) => (
                  <option key={index} value={index + 1}>{month}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label>Age from</Form.Label>
              <Form.Control
                type='number'
                value={filters.ageFrom}
                onChange={(e) => handleInputChange('ageFrom', e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Age to</Form.Label>
              <Form.Control
                type='number'
                value={filters.ageTo}
                onChange={(e) => handleInputChange('ageTo', e.target.value)}
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>Gender</Form.Label>
              <Select
                value={selectedGender}
                options={genderOptions}
                onChange={(selected) => handleSelectChange('gender', selected)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>State</Form.Label>
              <Select
                value={selectedState}
                options={stateOptions}
                onChange={(selected) => handleSelectChange('state', selected)}
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>Zip Code</Form.Label>
              <Form.Control
                type='text'
                value={filters.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Immigration Status</Form.Label>
              <Select
                value={selectedImmigration}
                options={immigrationOptions}
                onChange={handleImmigrationStatusChange}
                isMulti
                closeMenuOnSelect={false}
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>Contact Created From</Form.Label>
              <Form.Control
                type='date'
                value={filters.createdAfter}
                onChange={(e) => handleInputChange('createdAfter', e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Contact Created To</Form.Label>
              <Form.Control
                type='date'
                value={filters.createdBefore}
                onChange={(e) => handleInputChange('createdBefore', e.target.value)}
              />
            </Col>
          </Row>

          <hr />
          <h5>Signature Form Filters</h5>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>Signature Form Status</Form.Label>
              <Select
                value={selectedSignatureStatus}
                options={signatureStatusOptions}
                onChange={(selected) => handleSelectChange('signatureStatus', selected)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Signature Form</Form.Label>
              <Select
                value={selectedSignatureForm}
                options={signatureFormOptions}
                onChange={(selected) => handleSelectChange('signatureForm', selected)}
              />
            </Col>
          </Row>

          <hr />
          <h5>Policy Filters</h5>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>Carrier</Form.Label>
              <Select
                value={selectedCarrier}
                options={carrierOptions}
                onChange={(selected) => handleSelectChange('carrier', selected)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Policy Status</Form.Label>
              <Select
                value={selectedPolicyStatus}
                options={policyStatusOptions}
                onChange={(selected) => handleSelectChange('policyStatus', selected)}
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>Effective Date From</Form.Label>
              <Form.Control
                type='date'
                value={filters.effectiveDateAfter}
                onChange={(e) => handleInputChange('effectiveDateAfter', e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Effective Date To</Form.Label>
              <Form.Control
                type='date'
                value={filters.effectiveDateBefore}
                onChange={(e) => handleInputChange('effectiveDateBefore', e.target.value)}
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>Created Date From</Form.Label>
              <Form.Control
                type='date'
                value={filters.policyCreatedFrom}
                onChange={(e) => handleInputChange('policyCreatedFrom', e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Created Date To</Form.Label>
              <Form.Control
                type='date'
                value={filters.policyCreatedTo}
                onChange={(e) => handleInputChange('policyCreatedTo', e.target.value)}
              />
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label>Submission Date From</Form.Label>
              <Form.Control
                type='date'
                value={filters.submissionDateFrom}
                onChange={(e) => handleInputChange('submissionDateFrom', e.target.value)}
              />
            </Col>
            <Col md={6}>
              <Form.Label>Submission Date To</Form.Label>
              <Form.Control
                type='date'
                value={filters.submissionDateTo}
                onChange={(e) => handleInputChange('submissionDateTo', e.target.value)}
              />
            </Col>
          </Row>
            </>
          ) : null}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {activeTab === 'filters' ? (
          <>
            <Button variant='light' className='px-4' onClick={onClose}>
              Cancel
            </Button>
            <Button variant='dark' className='px-4' onClick={handleSubmit}>
              <i className='bi bi-search me-1' /> Apply Filters
            </Button>
          </>
        ) : null}
      </Modal.Footer>
    </Modal>
  )
}
